import React, { useMemo } from 'react';
import Image from 'next/legacy/image';
import { Asset } from 'contentful';

import { IAffiliateHeaderFields } from '../../../types/contentful';

import { getScaledImageHeight } from '../../utils/getScaledImageHeight';

import { Layout } from '../Layout';
import { Box } from '../Box';
import { GreenlightLogo } from '../GreenlightLogo';

import plusSign from '@/images/plus-sign-affiliates.svg';

const AFFILIATE_LOGO_MIN_HEIGHT_PX = 22;
const AFFILIATE_LOGO_MAX_HEIGHT_PX = 80;
const HEADER_HEIGHT_BASELINE_PX = 100;

export const getHeaderHeight = (asset?: Asset) => {
  const { file } = asset?.fields ?? {};

  const logoWidth = file?.details?.image?.width || 1;
  const logoHeight = file?.details?.image?.height || 1;

  const {
    imageHeight: affiliateLogoHeight,
    aspectRatio: affiliateLogoAspectRatio
  } = getScaledImageHeight(
    logoWidth,
    logoHeight,
    AFFILIATE_LOGO_MIN_HEIGHT_PX,
    AFFILIATE_LOGO_MAX_HEIGHT_PX
  );

  return {
    headerHeight:
      affiliateLogoAspectRatio > 3
        ? HEADER_HEIGHT_BASELINE_PX - 20
        : HEADER_HEIGHT_BASELINE_PX,
    affiliateLogoHeight
  };
};

export function AffiliateHeader({
  hideAffiliateLogo,
  affiliateLogo
}: IAffiliateHeaderFields) {
  const { file, description } = affiliateLogo?.fields ?? {};

  const { headerHeight, affiliateLogoHeight } = useMemo(
    () => getHeaderHeight(affiliateLogo),
    [affiliateLogo]
  );

  return (
    <div
      className="w-screen"
      style={{ height: `${headerHeight}px` }}
      data-testid="affiliate-header-1"
    >
      <Layout gutter={0}>
        <div className="max-w-[1440px] w-full">
          <div
            className="flex items-center mx-auto max-w-screen w-full px-6"
            style={{ height: `${headerHeight}px` }}
          >
            <Box flex="1 1 auto">
              <div className="max-w-full mx-auto desktop:mx-0">
                <GreenlightLogo isMenuOpen={false} />
              </div>
            </Box>
            {!hideAffiliateLogo && (
              <>
                <Image
                  src={plusSign}
                  alt="Plus sign"
                  className="mx-4 desktop:mx-6"
                />
                <Box flex="1 1 auto">
                  <img
                    src={file?.url}
                    style={{ maxHeight: affiliateLogoHeight }}
                    className="max-w-full"
                    alt={description ?? 'Affiliate of Greenlight logo'}
                  />
                </Box>
              </>
            )}
          </div>
        </div>
      </Layout>
    </div>
  );
}
